import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { materialEinheiten } from "./orderHelpers";

export const MaterialFormFields = ({ material, setMaterial, testIdPrefix = "material" }) => {
  const isArea = material.einheit === "m²";

  return (
    <>
      <div className="grid grid-cols-4 gap-2">
        <div className="space-y-1">
          <Label className="text-[10px]">{isArea ? "Fläche (m²)" : "Anzahl"}</Label>
          <Input
            type="number"
            min="0"
            step="0.1"
            value={material.anzahl}
            onChange={(e) => setMaterial({ ...material, anzahl: e.target.value })}
            placeholder={isArea ? "auto" : "5"}
            className="text-xs h-8"
            readOnly={isArea && !!material.flaeche_laenge && !!material.flaeche_breite}
            data-testid={`${testIdPrefix}-anzahl`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Einheit</Label>
          <Select
            value={material.einheit}
            onValueChange={(value) => setMaterial({ ...material, einheit: value })}
          >
            <SelectTrigger className="text-xs h-8" data-testid={`${testIdPrefix}-einheit`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {materialEinheiten.map((e) => (
                <SelectItem key={e} value={e} className="text-xs">{e}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Art.Nr.</Label>
          <Input
            value={material.artikelnummer}
            onChange={(e) => setMaterial({ ...material, artikelnummer: e.target.value })}
            placeholder="OIL-5W30"
            className="text-xs h-8"
            data-testid={`${testIdPrefix}-artikelnummer`}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Einzelpreis (VK €)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={material.einzelpreis || ""}
            onChange={(e) => {
              const vk = parseFloat(e.target.value) || 0;
              const ek = material.einkaufspreis || 0;
              const mp = ek > 0 ? Math.round((vk - ek) / ek * 10000) / 100 : material.marge_prozent;
              setMaterial({ ...material, einzelpreis: vk, marge_prozent: mp });
            }}
            className="text-xs h-8"
            data-testid={`${testIdPrefix}-einzelpreis`}
          />
        </div>
      </div>
      {/* EK / Marge% Info Row */}
      {(material.einkaufspreis > 0 || material.marge_prozent > 0) && (
        <div className="flex items-center gap-2 mt-1 text-[10px]" data-testid={`${testIdPrefix}-marge-info`}>
          <span className="text-muted-foreground">EK: {(material.einkaufspreis || 0).toFixed(2)} €</span>
          <span className="text-muted-foreground">|</span>
          <span className={`font-medium ${material.marge_prozent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            Marge: {(material.marge_prozent || 0).toFixed(1)}% ({((material.einzelpreis || 0) - (material.einkaufspreis || 0)).toFixed(2)} €)
          </span>
        </div>
      )}
      {/* Area calculation inputs for m² */}
      {isArea && (
        <div className="flex items-center gap-2 mt-2 p-2 bg-blue-50 rounded border border-blue-200" data-testid={`${testIdPrefix}-flaeche-section`}>
          <div className="space-y-1">
            <Label className="text-[10px]">Länge</Label>
            <Input type="number" step="0.1" placeholder="Länge" value={material.flaeche_laenge}
              onChange={e => setMaterial({...material, flaeche_laenge: e.target.value})}
              className="w-20 text-xs h-8" data-testid={`${testIdPrefix}-flaeche-laenge`} />
          </div>
          <span className="text-xs mt-4">x</span>
          <div className="space-y-1">
            <Label className="text-[10px]">Breite</Label>
            <Input type="number" step="0.1" placeholder="Breite" value={material.flaeche_breite}
              onChange={e => setMaterial({...material, flaeche_breite: e.target.value})}
              className="w-20 text-xs h-8" data-testid={`${testIdPrefix}-flaeche-breite`} />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px]">Einh.</Label>
            <Select value={material.flaeche_einheit} onValueChange={v => setMaterial({...material, flaeche_einheit: v})}>
              <SelectTrigger className="w-16 text-xs h-8" data-testid={`${testIdPrefix}-flaeche-einheit`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cm">cm</SelectItem>
                <SelectItem value="mm">mm</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {material.flaeche_laenge && material.flaeche_breite && (() => {
            let l = parseFloat(material.flaeche_laenge) || 0;
            let b = parseFloat(material.flaeche_breite) || 0;
            if (material.flaeche_einheit === "cm") { l /= 100; b /= 100; }
            else if (material.flaeche_einheit === "mm") { l /= 1000; b /= 1000; }
            const area = Math.round(l * b * 10000) / 10000;
            const preis = area * (parseFloat(material.einzelpreis) || 0);
            return (
              <span className="text-xs font-semibold text-blue-700 mt-4 whitespace-nowrap">
                = {area} m² {preis > 0 && `(${preis.toFixed(2)} €)`}
              </span>
            );
          })()}
        </div>
      )}
    </>
  );
};

export const emptyMaterial = () => ({
  bezeichnung: "",
  anzahl: "",
  einheit: "Stück",
  artikelnummer: "",
  einzelpreis: 0,
  einkaufspreis: 0,
  marge_prozent: 0,
  kosten: 0,
  material_id: "",
  flaeche_laenge: "",
  flaeche_breite: "",
  flaeche_einheit: "cm"
});

export const computeMaterialCost = (mat) => {
  const m = { ...mat };
  if (m.einheit === "m²" && m.flaeche_laenge && m.flaeche_breite) {
    let l = parseFloat(m.flaeche_laenge) || 0;
    let b = parseFloat(m.flaeche_breite) || 0;
    if (m.flaeche_einheit === "cm") { l /= 100; b /= 100; }
    else if (m.flaeche_einheit === "mm") { l /= 1000; b /= 1000; }
    m.anzahl = String(Math.round(l * b * 10000) / 10000);
  }
  const menge = parseFloat(m.anzahl) || 1;
  const ep = parseFloat(m.einzelpreis) || 0;
  m.kosten = Math.round(ep * menge * 100) / 100;
  return m;
};

export const materialFromInventory = (m, current) => ({
  ...current,
  bezeichnung: m.bezeichnung,
  artikelnummer: m.artikelnummer || "",
  einheit: m.einheit || "Stück",
  einzelpreis: m.verkaufspreis || 0,
  einkaufspreis: m.einkaufspreis || 0,
  marge_prozent: m.marge_prozent || 0,
  kosten: 0,
  material_id: m.id,
});
